import React from "react";
import { Link, Navigate, Route, Routes, useLocation } from "react-router-dom";
import {
  Box,
  Chip,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Switch,
  Typography,
} from "@mui/material";
import HandymanIcon from "@mui/icons-material/Handyman";
import LightModeIcon from "@mui/icons-material/LightMode";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import { toolRegistry } from "./toolRegistry";
import { useThemeMode } from "../context/ThemeModeContext";

export const DRAWER_WIDTH = 280;

export function AppShell() {
  const location = useLocation();
  const { mode, toggleMode } = useThemeMode();

  return (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      <Drawer
        variant="permanent"
        sx={{
          width: DRAWER_WIDTH,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: DRAWER_WIDTH,
            boxSizing: "border-box",
            backgroundColor: "action.hover",
            borderRight: "1px solid",
            borderColor: "divider",
          },
        }}
      >
        <Box sx={{ p: 3, display: "flex", flexDirection: "column", height: "100%" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3 }}>
            <HandymanIcon color="primary" />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Wisr Dev Kit
            </Typography>
          </Box>

          <List sx={{ flexGrow: 1 }}>
            {toolRegistry.map((tool) => {
              const selected = location.pathname.startsWith(tool.path);
              return (
                <ListItem key={tool.id} disablePadding>
                  <ListItemButton
                    component={Link}
                    to={tool.path}
                    selected={selected}
                    sx={{
                      borderRadius: 1,
                      mb: 1,
                      "&.Mui-selected": {
                        backgroundColor: "primary.main",
                        color: "white",
                        "&:hover": { backgroundColor: "primary.dark" },
                      },
                    }}
                  >
                    <ListItemIcon sx={{ color: "inherit", minWidth: 36 }}>{tool.icon}</ListItemIcon>
                    <ListItemText primary={tool.label} />
                    {tool.status === "comingSoon" && (
                      <Chip
                        label="Soon"
                        size="small"
                        sx={{
                          ml: 1,
                          backgroundColor: selected ? "rgba(255,255,255,0.2)" : "warning.light",
                          color: selected ? "white" : "warning.dark",
                        }}
                      />
                    )}
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              pt: 2,
              borderTop: "1px solid",
              borderColor: "divider",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {mode === "dark" ? (
                <DarkModeIcon fontSize="small" />
              ) : (
                <LightModeIcon fontSize="small" />
              )}
              <Typography variant="body2">
                {mode === "dark" ? "Dark mode" : "Light mode"}
              </Typography>
            </Box>
            <Switch
              checked={mode === "dark"}
              onChange={toggleMode}
              size="small"
              inputProps={{ "aria-label": "Toggle dark mode" }}
            />
          </Box>
        </Box>
      </Drawer>

      <Box
        component="main"
        sx={{ flexGrow: 1, p: 3, width: { sm: `calc(100% - ${DRAWER_WIDTH}px)` } }}
      >
        <Routes>
          <Route path="/" element={<Navigate to={toolRegistry[0].path} replace />} />
          {toolRegistry.map((tool) => (
            <Route key={tool.id} path={tool.path} element={tool.element} />
          ))}
        </Routes>
      </Box>
    </Box>
  );
}
